import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { SegmentedButtons } from 'react-native-paper';
import Svg, { Circle, Ellipse, G, Line, Path, Rect } from 'react-native-svg';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { UnitQty, useUnits } from '../../../context/UnitsContext';
import { C, FONT, SP } from '../../../theme/tokens';

const KEY = 'tab.calc.ductTraverse.v1';

type Shape = 'round' | 'rectangle' | 'flatOval';

type Fields = {
  shape: Shape;
  diameter: string;
  width: string;
  height: string;
};

const EMPTY: Fields = { shape: 'round', diameter: '', width: '', height: '' };

// ── Traverse point calculation (Equal Area / Log-Tchebycheff) ────────────────

function calculateTraversePoints(
  shape: Shape,
  dims: { width?: number; height?: number; diameter?: number },
): [number, number][] {
  if (shape === 'round' && dims.diameter) {
    const radius = dims.diameter / 2;
    if (dims.diameter <= 5) return [[0, 0]];

    let fractions: number[];
    if (dims.diameter <= 9) {
      fractions = [0.043, 0.146, 0.296, 0.704, 0.854, 0.957];
    } else if (dims.diameter <= 12) {
      fractions = [0.021, 0.117, 0.184, 0.345, 0.655, 0.816, 0.883, 0.979];
    } else {
      fractions = [0.019, 0.077, 0.153, 0.217, 0.361, 0.639, 0.783, 0.847, 0.923, 0.981];
    }

    const points: [number, number][] = [];
    fractions.forEach((f) => {
      const m = 1 - 2 * f;
      points.push([radius * m, 0]);
      points.push([0, radius * m]);
    });
    return points;
  }

  if (shape === 'rectangle' && dims.width && dims.height) {
    const points: [number, number][] = [];
    const getCount = (size: number) =>
      size < 12 ? 2 : Math.max(3, Math.floor(size / 6));

    const wCount = getCount(dims.width);
    const hCount = getCount(dims.height);

    for (let i = 0; i < wCount; i++) {
      for (let j = 0; j < hCount; j++) {
        const x = ((i + 0.5) / wCount - 0.5) * dims.width;
        const y = ((j + 0.5) / hCount - 0.5) * dims.height;
        points.push([x, y]);
      }
    }
    return points;
  }

  if (shape === 'flatOval' && dims.width && dims.height) {
    // SMACNA flat oval traverse:
    //   • Straight center section = equal-area grid over (W-H) × H
    //   • Each semicircular cap   = column of Log-Tchebycheff H-axis points at
    //     cap mid-depth (r/2 from the cap tip, inside the duct)
    const W = dims.width;
    const H = dims.height;
    const r = H / 2;
    const straight = Math.max(0, W - H);
    const points: [number, number][] = [];

    // 1. Straight section — equal-area grid
    const getCount = (size: number) =>
      size < 12 ? 2 : Math.max(3, Math.floor(size / 6));

    if (straight > 0) {
      const wCount = getCount(straight);
      const hCount = getCount(H);
      for (let i = 0; i < wCount; i++) {
        for (let j = 0; j < hCount; j++) {
          const x = ((i + 0.5) / wCount - 0.5) * straight;
          const y = ((j + 0.5) / hCount - 0.5) * H;
          points.push([x, y]);
        }
      }
    }

    // 2. Each cap — Log-Tchebycheff column at cap mid-depth.
    //    The cap is a half-disc of radius r; at x = r/2 inward from the tip
    //    the inside chord length is c = 2·√(r² − (r/2)²) = r·√3. Scale the
    //    Log-Tchebycheff fractions to that chord so every point stays inside
    //    the curved wall.
    const capX = straight / 2 + r / 2;
    const capChord = r * Math.sqrt(3);
    roundFractions(H).forEach((f) => {
      const y = (f - 0.5) * capChord;
      points.push([-capX, y]);
      points.push([capX, y]);
    });

    return points;
  }

  return [];
}

// ── SVG Visualization ────────────────────────────────────────────────────────

const VW = 300;
const VH = 220;
const CX = VW / 2;
const CY = VH / 2;
const MARGIN = 20;

type VizProps = {
  shape: Shape;
  dims: { diameter?: number; width?: number; height?: number };
  points: [number, number][];
};

function DuctVisualization({ shape, dims, points }: VizProps) {
  const scale = useMemo(() => {
    if (shape === 'round' && dims.diameter) {
      return Math.min((VW / 2 - MARGIN) / (dims.diameter / 2), (VH / 2 - MARGIN) / (dims.diameter / 2));
    }
    if ((shape === 'rectangle' || shape === 'flatOval') && dims.width && dims.height) {
      return Math.min((VW - MARGIN * 2) / dims.width, (VH - MARGIN * 2) / dims.height);
    }
    return 1;
  }, [shape, dims]);

  const ductShape = useMemo(() => {
    if (shape === 'round' && dims.diameter) {
      const r = (dims.diameter / 2) * scale;
      return <Ellipse cx={CX} cy={CY} rx={r} ry={r} fill="#142030" stroke="#DDEEFF" strokeWidth="1.5" />;
    }
    if (shape === 'rectangle' && dims.width && dims.height) {
      const sw = dims.width * scale;
      const sh = dims.height * scale;
      return <Rect x={CX - sw / 2} y={CY - sh / 2} width={sw} height={sh} fill="#142030" stroke="#DDEEFF" strokeWidth="1.5" />;
    }
    if (shape === 'flatOval' && dims.width && dims.height) {
      const r = (dims.height / 2) * scale;
      const straight = (dims.width - dims.height) * scale;
      const x0 = CX - (dims.width / 2) * scale;
      const x1 = CX + (dims.width / 2) * scale;
      const d = [
        `M ${x0} ${CY - r}`,
        `L ${x1 - r * 2} ${CY - r}`,
        `A ${r} ${r} 0 0 1 ${x1 - r * 2} ${CY + r}`,
        `L ${x0} ${CY + r}`,
        `A ${r} ${r} 0 0 1 ${x0} ${CY - r}`,
        'Z',
      ].join(' ');
      // Simpler flat oval: rounded rect approach
      const sw = dims.width * scale;
      const sh = dims.height * scale;
      return (
        <Rect
          x={CX - sw / 2}
          y={CY - sh / 2}
          width={sw}
          height={sh}
          rx={r}
          ry={r}
          fill="#142030"
          stroke="#DDEEFF"
          strokeWidth="1.5"
        />
      );
    }
    return null;
  }, [shape, dims, scale]);

  // Axis lines for round ducts
  const axisLines = useMemo(() => {
    if (shape !== 'round' || !dims.diameter) return null;
    const r = (dims.diameter / 2) * scale;
    return (
      <G opacity={0.25}>
        <Line x1={CX - r} y1={CY} x2={CX + r} y2={CY} stroke="#DDEEFF" strokeWidth="0.5" strokeDasharray="4,3" />
        <Line x1={CX} y1={CY - r} x2={CX} y2={CY + r} stroke="#DDEEFF" strokeWidth="0.5" strokeDasharray="4,3" />
      </G>
    );
  }, [shape, dims, scale]);

  return (
    <Svg width="100%" viewBox={`0 0 ${VW} ${VH}`} style={styles.svg}>
      {/* Background */}
      <Rect x={0} y={0} width={VW} height={VH} fill="#0D1B2A" />
      {ductShape}
      {axisLines}
      {points.map(([px, py], i) => (
        <Circle
          key={i}
          cx={CX + px * scale}
          cy={CY - py * scale}
          r={points.length === 1 ? 5 : 3.5}
          fill="#FF6B1A"
          opacity={0.9}
        />
      ))}
    </Svg>
  );
}

// ── Point Table ──────────────────────────────────────────────────────────────

function fmt(val: number, fromImp: (v: number, qty: UnitQty, d?: number) => string, unitLabel: string) {
  return `${fromImp(val, 'diameter', 2).replace(/\.?0+$/, '')} ${unitLabel}`;
}

/** Log-Tchebycheff fractions (distance from wall as fraction of diameter). */
function roundFractions(diameter: number): number[] {
  if (diameter <= 5) return [0.5];
  if (diameter <= 9) return [0.043, 0.146, 0.296, 0.704, 0.854, 0.957];
  if (diameter <= 12) return [0.021, 0.117, 0.184, 0.345, 0.655, 0.816, 0.883, 0.979];
  return [0.019, 0.077, 0.153, 0.217, 0.361, 0.639, 0.783, 0.847, 0.923, 0.981];
}

/** Equal-area grid positions from near wall, for one axis. */
function rectPositions(size: number): number[] {
  const count = size < 12 ? 2 : Math.max(3, Math.floor(size / 6));
  return Array.from({ length: count }, (_, i) => ((i + 0.5) / count) * size);
}

type TableProps = {
  shape: Shape;
  dims: { diameter?: number; width?: number; height?: number };
  fromImp: (v: number, qty: UnitQty, d?: number) => string;
  unitLabel: string;
};

function PointTable({ shape, dims, fromImp, unitLabel }: TableProps) {
  if (shape === 'round' && dims.diameter) {
    const fractions = roundFractions(dims.diameter);
    const positions = fractions.map((f) => f * dims.diameter!);
    return (
      <>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableCell, styles.tableHeaderText, { flex: 0.4 }]}>#</Text>
            <Text style={[styles.tableCell, styles.tableHeaderText, { flex: 1.2 }]}>From Wall</Text>
            <Text style={[styles.tableCell, styles.tableHeaderText, { flex: 1.2 }]}>% of Dia</Text>
          </View>
          {positions.map((p, i) => (
            <View key={i} style={[styles.tableRow, i % 2 === 0 && styles.tableRowAlt]}>
              <Text style={[styles.tableCell, styles.tableCellText, { flex: 0.4 }]}>{i + 1}</Text>
              <Text style={[styles.tableCell, styles.tableCellText, { flex: 1.2 }]}>{fmt(p, fromImp, unitLabel)}</Text>
              <Text style={[styles.tableCell, styles.tableCellText, { flex: 1.2 }]}>
                {(fractions[i] * 100).toFixed(1)}%
              </Text>
            </View>
          ))}
        </View>
        <Text style={styles.tableNote}>
          Insert probe from one side of the duct to each depth. Repeat all positions on the perpendicular axis (90°).
        </Text>
      </>
    );
  }

  if (shape === 'rectangle' && dims.width && dims.height) {
    const wPos = rectPositions(dims.width);
    const hPos = rectPositions(dims.height);
    return (
      <>
        <Text style={styles.subTableLabel}>Width axis — {wPos.length} positions from one edge</Text>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableCell, styles.tableHeaderText, { flex: 0.4 }]}>#</Text>
            <Text style={[styles.tableCell, styles.tableHeaderText, { flex: 2 }]}>From Edge</Text>
          </View>
          {wPos.map((p, i) => (
            <View key={i} style={[styles.tableRow, i % 2 === 0 && styles.tableRowAlt]}>
              <Text style={[styles.tableCell, styles.tableCellText, { flex: 0.4 }]}>W{i + 1}</Text>
              <Text style={[styles.tableCell, styles.tableCellText, { flex: 2 }]}>{fmt(p, fromImp, unitLabel)}</Text>
            </View>
          ))}
        </View>

        <Text style={[styles.subTableLabel, { marginTop: SP.md }]}>Height axis — {hPos.length} positions from one edge</Text>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableCell, styles.tableHeaderText, { flex: 0.4 }]}>#</Text>
            <Text style={[styles.tableCell, styles.tableHeaderText, { flex: 2 }]}>From Edge</Text>
          </View>
          {hPos.map((p, i) => (
            <View key={i} style={[styles.tableRow, i % 2 === 0 && styles.tableRowAlt]}>
              <Text style={[styles.tableCell, styles.tableCellText, { flex: 0.4 }]}>H{i + 1}</Text>
              <Text style={[styles.tableCell, styles.tableCellText, { flex: 2 }]}>{fmt(p, fromImp, unitLabel)}</Text>
            </View>
          ))}
        </View>
        <Text style={styles.tableNote}>
          Take one reading at every (Wi, Hj) intersection — {wPos.length} × {hPos.length} = {wPos.length * hPos.length} readings.
        </Text>
      </>
    );
  }

  if (shape === 'flatOval' && dims.width && dims.height) {
    const W = dims.width;
    const H = dims.height;
    const r = H / 2;
    const straight = Math.max(0, W - H);
    const wCount = straight > 0 ? (straight < 12 ? 2 : Math.max(3, Math.floor(straight / 6))) : 0;
    const hCount = H < 12 ? 2 : Math.max(3, Math.floor(H / 6));
    const wPos = Array.from({ length: wCount }, (_, i) => ((i + 0.5) / wCount) * straight);
    const hPosStraight = Array.from({ length: hCount }, (_, i) => ((i + 0.5) / hCount) * H);
    const capFractions = roundFractions(H);
    const capChord = r * Math.sqrt(3);
    const capOffsets = capFractions.map((f) => (f - 0.5) * capChord);
    const capDepth = r / 2;
    const straightPoints = wCount * hCount;
    const capPoints = capFractions.length * 2;

    return (
      <>
        <Text style={styles.subTableLabel}>
          Straight center section — {wCount} × {hCount} equal-area grid (section length {fmt(straight, fromImp, unitLabel)})
        </Text>
        {wCount > 0 && (
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCell, styles.tableHeaderText, { flex: 0.4 }]}>#</Text>
              <Text style={[styles.tableCell, styles.tableHeaderText, { flex: 2 }]}>From Straight/Cap Transition</Text>
            </View>
            {wPos.map((p, i) => (
              <View key={i} style={[styles.tableRow, i % 2 === 0 && styles.tableRowAlt]}>
                <Text style={[styles.tableCell, styles.tableCellText, { flex: 0.4 }]}>W{i + 1}</Text>
                <Text style={[styles.tableCell, styles.tableCellText, { flex: 2 }]}>{fmt(p, fromImp, unitLabel)}</Text>
              </View>
            ))}
          </View>
        )}
        <Text style={[styles.subTableLabel, { marginTop: SP.md }]}>
          Height (straight section) — {hCount} positions from one flat edge
        </Text>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableCell, styles.tableHeaderText, { flex: 0.4 }]}>#</Text>
            <Text style={[styles.tableCell, styles.tableHeaderText, { flex: 2 }]}>From Edge</Text>
          </View>
          {hPosStraight.map((p, i) => (
            <View key={i} style={[styles.tableRow, i % 2 === 0 && styles.tableRowAlt]}>
              <Text style={[styles.tableCell, styles.tableCellText, { flex: 0.4 }]}>H{i + 1}</Text>
              <Text style={[styles.tableCell, styles.tableCellText, { flex: 2 }]}>{fmt(p, fromImp, unitLabel)}</Text>
            </View>
          ))}
        </View>

        <Text style={[styles.subTableLabel, { marginTop: SP.md }]}>
          End caps — {capFractions.length} points per cap (Log-Tchebycheff), probe depth {fmt(capDepth, fromImp, unitLabel)} from cap tip
        </Text>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableCell, styles.tableHeaderText, { flex: 0.4 }]}>#</Text>
            <Text style={[styles.tableCell, styles.tableHeaderText, { flex: 1.6 }]}>Offset from Centerline</Text>
          </View>
          {capOffsets.map((p, i) => (
            <View key={i} style={[styles.tableRow, i % 2 === 0 && styles.tableRowAlt]}>
              <Text style={[styles.tableCell, styles.tableCellText, { flex: 0.4 }]}>C{i + 1}</Text>
              <Text style={[styles.tableCell, styles.tableCellText, { flex: 1.6 }]}>
                {p >= 0 ? '+' : '−'}{fmt(Math.abs(p), fromImp, unitLabel)}
              </Text>
            </View>
          ))}
        </View>

        <Text style={styles.tableNote}>
          Straight: one reading at every (Wi, Hj) intersection — {wCount} × {hCount} = {straightPoints} readings.{'\n'}
          Caps: on each end, insert probe to depth {fmt(capDepth, fromImp, unitLabel)} and sample at each C position — {capFractions.length} × 2 = {capPoints} readings.{'\n'}
          Total: {straightPoints + capPoints} readings.
        </Text>
      </>
    );
  }

  return null;
}

// ── Main Screen ──────────────────────────────────────────────────────────────

export default function DuctTraverseScreen() {
  const [rawFields, setFields, { clear }] = usePersistedState<Fields>(KEY, EMPTY);
  // Flat oval is temporarily disabled — coerce any persisted 'flatOval' to 'round'.
  const fields: Fields = rawFields.shape === 'flatOval' ? { ...rawFields, shape: 'round' } : rawFields;
  const { unit, toImp, fromImp } = useUnits();
  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const dims = useMemo(() => {
    if (fields.shape === 'round') {
      const d = toImp(fields.diameter, 'diameter');
      return d > 0 ? { diameter: d } : {};
    }
    const w = toImp(fields.width, 'diameter');
    const h = toImp(fields.height, 'diameter');
    return w > 0 && h > 0 ? { width: w, height: h } : {};
  }, [fields.shape, fields.diameter, fields.width, fields.height]);

  const points = useMemo(
    () => calculateTraversePoints(fields.shape, dims),
    [fields.shape, dims],
  );

  const hasDims = Object.keys(dims).length > 0;
  const unitLabel = unit('diameter');

  const pointCountSummary = useMemo(() => {
    if (!hasDims) return null;
    if (fields.shape === 'round') {
      const d = (dims as any).diameter as number;
      if (d <= 5) return '1 point (centerline only — duct ≤5")';
      const perAxis = points.length / 2;
      return `${points.length} points (${perAxis}-pt Log-Tchebycheff, 2 axes)`;
    }
    if (fields.shape === 'rectangle') {
      const wCount = dims.width ? (dims.width < 12 ? 2 : Math.max(3, Math.floor(dims.width / 6))) : 0;
      const hCount = dims.height ? (dims.height < 12 ? 2 : Math.max(3, Math.floor(dims.height / 6))) : 0;
      return `${points.length} points (${wCount} × ${hCount} equal-area grid)`;
    }
    const W = dims.width ?? 0;
    const H = dims.height ?? 0;
    const straight = Math.max(0, W - H);
    const wCount = straight > 0 ? (straight < 12 ? 2 : Math.max(3, Math.floor(straight / 6))) : 0;
    const hCount = H < 12 ? 2 : Math.max(3, Math.floor(H / 6));
    const capPerSide = H <= 5 ? 1 : H <= 9 ? 6 : H <= 12 ? 8 : 10;
    return `${points.length} points (${wCount}×${hCount} straight grid + 2×${capPerSide} caps)`;
  }, [fields.shape, dims, points, hasDims]);

  return (
    <CalcShell
      title="Duct Traverse"
      subtitle="TAB · Airflow"
      formula={
        <>
          <FormulaLine>Round: Log-Tchebycheff positions</FormulaLine>
          <FormulaLine>Rect: Equal-area grid (6" spacing, min 3 pts/side)</FormulaLine>
          <FormulaNote>
            Diameters ≤5": 1 centerline pt{'\n'}
            6–9": 6-pt per axis · 10–12": 8-pt · &gt;12": 10-pt
          </FormulaNote>
        </>
      }
      onClear={() => { void clear(); }}
    >
      <SegmentedButtons
        value={fields.shape}
        onValueChange={(v) => set({ shape: v as Shape })}
        buttons={[
          { value: 'round', label: 'Round' },
          { value: 'rectangle', label: 'Rect' },
        ]}
        style={{ marginBottom: 12 }}
      />

      {fields.shape === 'round' ? (
        <NumericField
          label="Duct Diameter"
          value={fields.diameter}
          onChangeText={(v) => set({ diameter: v })}
          rightText={unitLabel}
          maxDecimals={2}
        />
      ) : (
        <>
          <NumericField
            label="Duct Width"
            value={fields.width}
            onChangeText={(v) => set({ width: v })}
            rightText={unitLabel}
            maxDecimals={2}
          />
          <View style={{ height: 8 }} />
          <NumericField
            label="Duct Height"
            value={fields.height}
            onChangeText={(v) => set({ height: v })}
            rightText={unitLabel}
            maxDecimals={2}
          />
        </>
      )}

      {hasDims && points.length > 0 && (
        <>
          <View style={styles.sectionRow}>
            <Text style={styles.sectionLabel}>Traverse Layout</Text>
          </View>
          <View style={styles.vizCard}>
            <DuctVisualization shape={fields.shape} dims={dims} points={points} />
          </View>

          <View style={styles.summaryRow}>
            <Text style={styles.summaryText}>{pointCountSummary}</Text>
          </View>

          <Text style={[styles.sectionLabel, { marginTop: SP.md }]}>Measurement Locations</Text>
          <PointTable
            shape={fields.shape}
            dims={dims}
            fromImp={fromImp}
            unitLabel={unitLabel}
          />
        </>
      )}
    </CalcShell>
  );
}

const styles = StyleSheet.create({
  sectionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: SP.md,
    marginBottom: SP.sm,
  },
  sectionLabel: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: SP.sm,
  },
  vizCard: {
    borderRadius: 10,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: C.edge,
    marginBottom: SP.sm,
  },
  svg: {
    width: '100%',
    aspectRatio: VW / VH,
  },
  summaryRow: {
    backgroundColor: C.panel,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: C.edge,
    borderLeftWidth: 3,
    borderLeftColor: C.accent,
    paddingHorizontal: SP.md,
    paddingVertical: 10,
    marginBottom: SP.sm,
  },
  summaryText: {
    fontFamily: FONT.mono,
    fontSize: 13,
    color: C.ink,
  },
  table: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: C.edge,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: C.panel,
    paddingVertical: 8,
    paddingHorizontal: SP.sm,
    borderBottomWidth: 1,
    borderBottomColor: C.edge,
  },
  tableHeaderText: {
    fontFamily: FONT.mono,
    fontSize: 11,
    color: C.dim,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 9,
    paddingHorizontal: SP.sm,
  },
  tableRowAlt: {
    backgroundColor: C.panel,
  },
  tableCell: {
    flex: 1,
    paddingHorizontal: 4,
  },
  tableCellText: {
    fontFamily: FONT.mono,
    fontSize: 13,
    color: C.ink,
  },
  subTableLabel: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.ink2,
    marginBottom: SP.xs,
  },
  tableNote: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    lineHeight: 17,
    marginTop: SP.sm,
  },
});
